/**
 * Centralized RBAC permission helpers for the admin portal.
 * Mirrors the backend roles.json permission structure.
 * 
 * Usage:
 *   import { canViewBilling, canManageUsers, isAdminOrAbove } from '@/config/permissions';
 *   if (canViewBilling(role)) { ... }
 */

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'USER';

// ─── Role hierarchy checks ──────────────────────────────────────────
export const isSuperAdmin = (role: string): boolean => role === 'SUPER_ADMIN';
export const isAdmin = (role: string): boolean => role === 'ADMIN';
export const isManager = (role: string): boolean => role === 'MANAGER';
export const isAdminOrAbove = (role: string): boolean => role === 'SUPER_ADMIN' || role === 'ADMIN';
export const isManagerOrAbove = (role: string): boolean => role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MANAGER';

// ─── Feature-level permission checks ────────────────────────────────
/** Can view and manage billing / credit usage pages */
export const canViewBilling = (role: string): boolean => isAdminOrAbove(role);

/** Can create, edit, archive, and delete users */
export const canManageUsers = (role: string): boolean => isAdminOrAbove(role);

/** Can view all teams across the tenant (vs only assigned teams) */
export const canViewAllTeams = (role: string): boolean => isAdminOrAbove(role);

/** Can view and edit the company profile (My Organization) */
export const canEditCompanyProfile = (role: string): boolean => isAdminOrAbove(role);

/** Can view the company profile in read-only mode */
export const canViewCompanyProfile = (role: string): boolean => isManagerOrAbove(role);

/** Can access the full meeting dashboard */
export const canViewMeetingDashboard = (role: string): boolean => isManagerOrAbove(role);

/** Can access guided practice scenarios management */
export const canViewPracticeEngine = (role: string): boolean => isManagerOrAbove(role);

/** Can access digital twin templates management */
export const canViewDigitalTwins = (role: string): boolean => isAdminOrAbove(role);

/** Can access CRM / integration settings */
export const canViewCRMSettings = (role: string): boolean => isAdminOrAbove(role);

/** Can manage subscription plans */
export const canViewSubscription = (role: string): boolean => isSuperAdmin(role);

/** Can view organizations list */
export const canViewOrganizations = (role: string): boolean => isSuperAdmin(role);

/** Can view admin/contact requests */
export const canViewAdminRequests = (role: string): boolean => isSuperAdmin(role);
